import React, { useState, useEffect, useRef } from 'react';
import { authAPI } from '../../services/api';

const inputClass = "w-full border border-[#E5E7EB] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/15 focus:border-[#1E40AF] bg-white transition-all";
const pinInputClass = `${inputClass} text-center text-2xl font-bold tracking-[0.5em]`;

// Returning-passenger phone + PIN login, with a forgot-PIN recovery
// sub-flow (re-prove phone control via WhatsApp, same mechanism as
// registration, then set a new PIN). Mirrors passenger_app's
// pin_login_screen.dart + forgot_pin_screen.dart.
// 'login' → phone+PIN. 'forgotPhone' → re-enter phone. 'forgotWaiting' →
// show code, poll. 'forgotNewPin' → set replacement PIN.
const PinLoginPanel = ({ navigate, returnTo }) => {
  const [step, setStep] = useState('login');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');

  const [verificationId, setVerificationId] = useState(null);
  const [code, setCode] = useState(null);
  const [deepLink, setDeepLink] = useState(null);
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const friendlyError = (err) => err.response?.data?.message || 'حدث خطأ، حاول مرة أخرى';

  const login = async (e) => {
    e.preventDefault();
    if (!phone.trim() || pin.length !== 6) {
      setError('يرجى إدخال رقم الهاتف ورمز PIN المكوّن من 6 أرقام'); return;
    }
    setError(''); setLoading(true);
    try {
      const res = await authAPI.pinLogin({ phone: phone.trim(), pin });
      const { token, ...safeUser } = res.data.data;
      localStorage.setItem('passenger_user', JSON.stringify(safeUser));
      navigate(returnTo || '/');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const startForgot = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { setError('يرجى إدخال رقم الهاتف'); return; }
    setError(''); setLoading(true);
    // Reserved synchronously, inside this click handler — see
    // WhatsAppRegisterPanel.jsx's identical comment for why.
    const whatsappWindow = window.open('', '_blank');
    try {
      const res = await authAPI.startForgotPin({ phone: phone.trim() });
      const data = res.data.data;
      setVerificationId(data.verificationId);
      setCode(data.code);
      setDeepLink(data.deepLink);
      setStep('forgotWaiting');
      startPolling(data.verificationId);
      if (data.deepLink && whatsappWindow) whatsappWindow.location.href = data.deepLink;
      else if (whatsappWindow) whatsappWindow.close();
    } catch (err) {
      if (whatsappWindow) whatsappWindow.close();
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await authAPI.getWhatsAppVerificationStatus(id);
        const status = res.data.status;
        if (status === 'VERIFIED') {
          clearInterval(pollRef.current);
          setStep('forgotNewPin');
        } else if (status === 'EXPIRED' || status === 'FAILED') {
          clearInterval(pollRef.current);
          setStep('forgotPhone');
          setError(status === 'EXPIRED'
            ? 'انتهت صلاحية رمز التحقق — حاول من جديد'
            : 'تعذّر التحقق — تأكد من إرسال الرمز الصحيح وحاول من جديد');
        }
      } catch { /* transient — keep polling */ }
    }, 3000);
  };

  const submitNewPin = async (e) => {
    e.preventDefault();
    if (newPin.length !== 6) { setError('رمز PIN يجب أن يكون 6 أرقام'); return; }
    if (newPin !== confirmNewPin) { setError('الرمزان غير متطابقين'); return; }
    setError(''); setLoading(true);
    try {
      const res = await authAPI.completeForgotPin({ verificationId, newPin });
      const { token, ...safeUser } = res.data.data;
      localStorage.setItem('passenger_user', JSON.stringify(safeUser));
      navigate(returnTo || '/');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 text-red-600 rounded-xl px-4 py-3 text-sm">
          <span className="text-lg">⚠️</span><span>{error}</span>
        </div>
      )}

      {step === 'login' && (
        <form onSubmit={login} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">رقم الهاتف</label>
            <input type="tel" placeholder="+249 91 234 5678" required autoFocus value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} dir="ltr" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">الرقم السري (PIN)</label>
            <input type="password" inputMode="numeric" maxLength={6} placeholder="••••••" required
              value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} className={pinInputClass} dir="ltr" />
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition-all">
            {loading ? '...جارٍ الدخول' : 'تسجيل الدخول'}
          </button>
          <button type="button" onClick={() => { setStep('forgotPhone'); setError(''); }}
            className="w-full text-center text-xs text-[#1E40AF] hover:underline font-semibold">
            نسيت الرقم السري؟
          </button>
        </form>
      )}

      {step === 'forgotPhone' && (
        <form onSubmit={startForgot} className="space-y-4">
          <p className="text-sm font-semibold text-gray-800 text-center">أدخل رقم هاتفك لإعادة تعيين رمز PIN</p>
          <input type="tel" placeholder="+249 91 234 5678" required autoFocus value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} dir="ltr" />
          <button type="submit" disabled={loading}
            className="w-full bg-[#16A34A] hover:bg-[#15803D] disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition-all">
            {loading ? '...جارٍ الإرسال' : 'إرسال رمز التحقق'}
          </button>
          <button type="button" onClick={() => { setStep('login'); setError(''); }}
            className="w-full text-center text-xs text-gray-500 hover:underline font-semibold">
            رجوع لتسجيل الدخول
          </button>
        </form>
      )}

      {step === 'forgotWaiting' && (
        <div className="space-y-4 text-center">
          <p className="text-sm font-semibold text-gray-800">فتح واتساب تلقائياً برسالة جاهزة</p>
          <p className="text-xs text-gray-500">إن لم تُفتح نافذة واتساب (قد يحجبها المتصفح)، اضغط الزر أدناه أو أرسل الرمز يدوياً</p>
          <div className="bg-gray-50 border border-gray-200 rounded-xl py-4">
            <span className="text-xl font-bold tracking-widest text-[#1E40AF]" dir="ltr">SAFER-VFY-{code}</span>
          </div>
          <a href={deepLink || '#'} target="_blank" rel="noopener noreferrer"
            className="block w-full bg-[#16A34A] hover:bg-[#15803D] text-white font-bold py-3.5 rounded-xl transition-all">
            إعادة فتح واتساب
          </a>
          <p className="text-xs text-gray-500">بانتظار التأكيد...</p>
        </div>
      )}

      {step === 'forgotNewPin' && (
        <form onSubmit={submitNewPin} className="space-y-4">
          <p className="text-sm font-semibold text-gray-800 text-center">تم التحقق — أدخل رمز PIN الجديد</p>
          <input type="password" inputMode="numeric" maxLength={6} placeholder="رمز PIN الجديد" required
            value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))} className={pinInputClass} dir="ltr" />
          <input type="password" inputMode="numeric" maxLength={6} placeholder="تأكيد الرمز" required
            value={confirmNewPin} onChange={(e) => setConfirmNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))} className={pinInputClass} dir="ltr" />
          <button type="submit" disabled={loading}
            className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition-all">
            {loading ? '...جارٍ التأكيد' : 'تأكيد'}
          </button>
        </form>
      )}
    </div>
  );
};

export default PinLoginPanel;
